import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2 } from 'lucide-react';
import TurnstileWidget from './TurnstileWidget';
import { triggerEmail } from '../services/emailService';
import { trackSignup } from '../services/analyticsService';

const Auth = ({ supabase, onLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('signup');
    const [turnstileToken, setTurnstileToken] = useState(null);

    // Reset turnstile token when switching tabs
    useEffect(() => {
        setTurnstileToken(null);
        setError(null);
    }, [activeTab]);

    const handleEmailAuth = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            if (activeTab === 'signup') {
                const { data, error } = await supabase.auth.signUp({
                    email,
                    password,
                });
                if (error) throw error;
                // Track signup analytics
                trackSignup(data.user?.id, email);
                // Send welcome email
                triggerEmail('welcome', email);
                alert('Success! Please check your email to confirm your account.');
                setActiveTab('login');
                setEmail('');
                setPassword('');
            } else {
                const { data, error } = await supabase.auth.signInWithPassword({
                    email,
                    password,
                });
                if (error) throw error;
                if (data.user) {
                    onLogin(data.user);
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false);

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: window.location.origin + '/reset-password',
            });
            if (error) throw error;
            setResetEmailSent(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const [githubLoading, setGithubLoading] = useState(false);

    const handleGoogleAuth = async () => {
        setGoogleLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: window.location.origin
                }
            });
            if (error) throw error;
        } catch (err) {
            setError(err.message);
            setGoogleLoading(false);
        }
    };

    const handleGithubAuth = async () => {
        setGithubLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'github',
                options: {
                    redirectTo: window.location.origin
                }
            });
            if (error) throw error;
        } catch (err) {
            setError(err.message);
            setGithubLoading(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex flex-col bg-background">
            {/* Christmas Banner */}
            <div className="bg-gradient-to-r from-green-700 via-red-600 to-green-700 text-white py-2.5 px-4 text-center text-sm font-medium shadow-sm sticky top-0 z-50 flex items-center justify-center gap-3">
                <span className="animate-pulse">🎄</span>
                <span>
                    Christmas Special: Use code <span className="font-bold font-mono bg-white/20 px-1.5 py-0.5 rounded mx-1">CHRISTMAS25</span> for 30% off all plans!
                </span>
                <span className="hidden sm:inline">🎁</span>
                <button
                    onClick={() => {
                        navigator.clipboard.writeText('CHRISTMAS25');
                        const btn = document.getElementById('copy-btn-icon');
                        if (btn) {
                            btn.style.opacity = '0';
                            setTimeout(() => {
                                btn.innerHTML = '<polyline points="20 6 9 17 4 12"></polyline>';
                                btn.style.opacity = '1';
                            }, 150);
                            setTimeout(() => {
                                btn.style.opacity = '0';
                                setTimeout(() => {
                                    btn.innerHTML = '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path>';
                                    btn.style.opacity = '1';
                                }, 150);
                            }, 2000);
                        }
                        // Simple toast fallback since we might not have the icon logic perfect inline without state
                        const toast = document.createElement('div');
                        toast.textContent = 'Code copied!';
                        toast.style.position = 'fixed';
                        toast.style.top = '50px';
                        toast.style.left = '50%';
                        toast.style.transform = 'translate(-50%, 0)';
                        toast.style.padding = '8px 16px';
                        toast.style.background = '#000';
                        toast.style.color = '#fff';
                        toast.style.borderRadius = '20px';
                        toast.style.fontSize = '12px';
                        toast.style.zIndex = '100';
                        toast.style.opacity = '0';
                        toast.style.transition = 'opacity 0.3s';
                        document.body.appendChild(toast);
                        requestAnimationFrame(() => toast.style.opacity = '1');
                        setTimeout(() => {
                            toast.style.opacity = '0';
                            setTimeout(() => toast.remove(), 300);
                        }, 2000);
                    }}
                    className="ml-2 p-1.5 hover:bg-white/20 rounded-md transition-colors group flex items-center gap-1.5 text-xs bg-white/10 border border-white/20"
                    title="Copy code"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        id="copy-btn-icon"
                        style={{ transition: 'opacity 0.15s' }}
                    >
                        <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                        <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                    </svg>
                    <span className="font-semibold">Copy</span>
                </button>
            </div>

            <div className="flex-1 flex w-full relative">
                {/* Left Side - Auth Form */}
                <div className="flex-1 flex items-center justify-center p-4 md:p-8 relative overflow-y-auto">
                    <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:50px_50px]" />
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-purple-500/5 pointer-events-none" />

                    <div className="w-full max-w-md relative z-10 my-8 space-y-8">
                        {/* Header */}
                        <div className="text-center space-y-2">
                            <div className="flex items-center justify-center mb-4">
                                <img src="/logo.png" alt="Pop Onboarding" className="h-16 w-16" />
                            </div>
                            <h1 className="text-3xl font-bold tracking-tight">Welcome to Pop Onboarding</h1>
                            {/* AI badge hidden - code preserved for future reactivation
                            <div className="flex items-center justify-center gap-2 mt-2">
                                <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2.5 py-0.5 rounded-full border border-purple-200">
                                    New: AI Generation ✨
                                </span>
                            </div>
                            */}
                            <p className="text-muted-foreground mt-2">
                                Create beautiful onboarding flows for your apps in seconds.
                            </p>
                        </div>

                        {/* Form Content */}
                        <div className="space-y-6">
                            {showForgotPassword ? (
                                <div className="space-y-4">
                                    <div className="text-center space-y-2">
                                        <h3 className="font-semibold">Reset Password</h3>
                                        <p className="text-sm text-muted-foreground">
                                            Enter your email address and we'll send you a link to reset your password.
                                        </p>
                                    </div>

                                    {resetEmailSent ? (
                                        <div className="space-y-4">
                                            <div className="p-3 text-sm text-green-600 bg-green-50 border border-green-200 rounded-md">
                                                Check your email for the password reset link.
                                            </div>
                                            <Button
                                                variant="outline"
                                                className="w-full"
                                                onClick={() => {
                                                    setShowForgotPassword(false);
                                                    setResetEmailSent(false);
                                                    setError(null);
                                                }}
                                            >
                                                Back to Login
                                            </Button>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleForgotPassword} className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="reset-email">Email</Label>
                                                <Input
                                                    id="reset-email"
                                                    type="email"
                                                    placeholder="you@example.com"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                    disabled={loading}
                                                />
                                            </div>

                                            {error && (
                                                <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                                                    {error}
                                                </div>
                                            )}

                                            <Button type="submit" className="w-full" disabled={loading}>
                                                {loading ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Sending link...
                                                    </>
                                                ) : (
                                                    'Send Reset Link'
                                                )}
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                className="w-full"
                                                onClick={() => {
                                                    setShowForgotPassword(false);
                                                    setError(null);
                                                }}
                                            >
                                                Back to Login
                                            </Button>
                                        </form>
                                    )}
                                </div>
                            ) : (
                                <>
                                    <div className="flex flex-col gap-2">
                                        {/* Google OAuth Button */}
                                        <Button
                                            variant="outline"
                                            className="w-full h-11"
                                            onClick={handleGoogleAuth}
                                            disabled={googleLoading || githubLoading || loading}
                                        >
                                            {googleLoading ? (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            ) : (
                                                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                                                    <path
                                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                                        fill="#4285F4"
                                                    />
                                                    <path
                                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                                        fill="#34A853"
                                                    />
                                                    <path
                                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                                        fill="#FBBC05"
                                                    />
                                                    <path
                                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                                        fill="#EA4335"
                                                    />
                                                </svg>
                                            )}
                                            Continue with Google
                                        </Button>

                                        {/* GitHub OAuth Button */}
                                        <Button
                                            variant="outline"
                                            className="w-full h-11"
                                            onClick={handleGithubAuth}
                                            disabled={googleLoading || githubLoading || loading}
                                        >
                                            {githubLoading ? (
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            ) : (
                                                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                                                    <path
                                                        d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.76.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            )}
                                            Continue with GitHub
                                        </Button>
                                    </div>

                                    {/* Divider */}
                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <span className="w-full border-t" />
                                        </div>
                                        <div className="relative flex justify-center text-xs uppercase">
                                            <span className="bg-background px-2 text-muted-foreground">
                                                Or continue with email
                                            </span>
                                        </div>
                                    </div>

                                    {/* Email/Password Tabs */}
                                    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                                        <TabsList className="grid w-full grid-cols-2">
                                            <TabsTrigger value="login">Login</TabsTrigger>
                                            <TabsTrigger value="signup">Sign Up</TabsTrigger>
                                        </TabsList>

                                        <TabsContent value="login" className="space-y-4 mt-4">
                                            <form onSubmit={handleEmailAuth} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="login-email">Email</Label>
                                                    <Input
                                                        id="login-email"
                                                        type="email"
                                                        placeholder="you@example.com"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        required
                                                        disabled={loading || googleLoading}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <div className="flex items-center justify-between">
                                                        <Label htmlFor="login-password">Password</Label>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setShowForgotPassword(true);
                                                                setError(null);
                                                            }}
                                                            className="text-xs text-primary hover:underline"
                                                        >
                                                            Forgot password?
                                                        </button>
                                                    </div>
                                                    <Input
                                                        id="login-password"
                                                        type="password"
                                                        placeholder="••••••••"
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        required
                                                        disabled={loading || googleLoading}
                                                    />
                                                </div>

                                                {/* Turnstile Widget */}
                                                <div className="flex justify-center py-2">
                                                    <TurnstileWidget
                                                        onSuccess={setTurnstileToken}
                                                        onError={() => setError('Security verification failed. Please try again.')}
                                                        action="login"
                                                    />
                                                </div>

                                                {error && (
                                                    <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                                                        {error}
                                                    </div>
                                                )}
                                                <Button type="submit" className="w-full" disabled={loading || googleLoading || !turnstileToken}>
                                                    {loading ? (
                                                        <>
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                            Logging in...
                                                        </>
                                                    ) : (
                                                        'Login'
                                                    )}
                                                </Button>
                                            </form>
                                        </TabsContent>

                                        <TabsContent value="signup" className="space-y-4 mt-4">
                                            <form onSubmit={handleEmailAuth} className="space-y-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="signup-email">Email</Label>
                                                    <Input
                                                        id="signup-email"
                                                        type="email"
                                                        placeholder="you@example.com"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        required
                                                        disabled={loading || googleLoading}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="signup-password">Password</Label>
                                                    <Input
                                                        id="signup-password"
                                                        type="password"
                                                        placeholder="Minimum 6 characters"
                                                        value={password}
                                                        onChange={(e) => setPassword(e.target.value)}
                                                        required
                                                        minLength={6}
                                                        disabled={loading || googleLoading}
                                                    />
                                                </div>

                                                {/* Turnstile Widget */}
                                                <div className="flex justify-center py-2">
                                                    <TurnstileWidget
                                                        onSuccess={setTurnstileToken}
                                                        onError={() => setError('Security verification failed. Please try again.')}
                                                        action="signup"
                                                    />
                                                </div>

                                                {error && (
                                                    <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                                                        {error}
                                                    </div>
                                                )}
                                                <Button type="submit" className="w-full" disabled={loading || googleLoading || !turnstileToken}>
                                                    {loading ? (
                                                        <>
                                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                            Creating account...
                                                        </>
                                                    ) : (
                                                        'Create Account'
                                                    )}
                                                </Button>
                                            </form>
                                        </TabsContent>
                                    </Tabs>

                                    <p className="text-xs text-center text-muted-foreground">
                                        By continuing, you agree to our <a href="https://poponboarding.com/terms.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">Terms of Service</a> and <a href="https://poponboarding.com/privacy.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">Privacy Policy</a>
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Side - Dashboard Preview */}
                <div className="hidden lg:flex flex-1 relative bg-white border-l border-border/50 items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-white to-purple-500/5" />
                    <div className="absolute inset-0 bg-grid-black/[0.02] bg-[size:50px_50px]" />
                    <img
                        src="/editor-main.gif"
                        alt="Dashboard Preview"
                        className="relative z-10 w-[90%] h-auto rounded-lg shadow-2xl border border-border/50"
                    />
                </div>
            </div>
        </div>
    );
};

export default Auth;
